import type { IconName } from '@launchpad-ui/icons';

export enum MetricKind {
  CLICK = 'click',
  PAGEVIEW = 'pageview',
  CUSTOM = 'custom',
  NUMERIC = 'numeric',
  BINARY = 'binary',
  COUNT = 'count',
  COUNT_DISTINCT = 'count_distinct',
  TRACE = 'trace',
}

export enum MetricGroupKind {
  FUNNEL_GROUP = 'funnel',
  STANDARD_GROUP = 'standard',
}

export type MetricDisplay = {
  displayName: string;
};

export type MetricGroupDisplay = {
  displayName: string;
  displayIcon: IconName;
};

export const MetricKindDisplays: { [key: string]: MetricDisplay } = {
  [MetricKind.CLICK]: { displayName: 'Clicked or tapped' },
  [MetricKind.PAGEVIEW]: { displayName: 'Page view' },
  [MetricKind.CUSTOM]: { displayName: 'Custom: conversion' },
  [MetricKind.COUNT]: { displayName: 'Count' },
  [MetricKind.COUNT_DISTINCT]: { displayName: 'Count distinct' },
  [MetricKind.BINARY]: { displayName: 'Binary' },
  [MetricKind.NUMERIC]: { displayName: 'Numeric' },
};

export const MetricGroupKindDisplays: { [key: string]: MetricGroupDisplay } = {
  [MetricGroupKind.STANDARD_GROUP]: { displayName: 'Standard', displayIcon: 'metric-group' },
  [MetricGroupKind.FUNNEL_GROUP]: { displayName: 'Funnel', displayIcon: 'metric-funnel' },
};

export function getMetricGroupKindDisplay(kind: string): MetricGroupDisplay | undefined {
  return MetricGroupKindDisplays[kind];
}

export function getMetricKindDisplay(
  metricKind: string,
  unitAggregationType?: 'sum' | 'average' | 'count_distinct',
  isNumeric?: boolean,
): MetricDisplay | MetricGroupDisplay {
  const kind = getMetricKind(metricKind, unitAggregationType, isNumeric);
  return MetricKindDisplays[kind] ?? MetricGroupKindDisplays[kind];
}

export const getMetricKind = (
  kindString: string,
  unitAggregationType?: 'sum' | 'average' | 'count_distinct',
  isNumeric?: boolean,
) => {
  if (unitAggregationType || isNumeric) {
    if (kindString === 'click') {
      return MetricKind.CLICK;
    } else if (kindString === 'pageview') {
      return MetricKind.PAGEVIEW;
    } else if (kindString === 'custom' || kindString === 'trace') {
      // count_distinct is checked before isNumeric: its metrics are numeric, but
      // they have their own kind display.
      if (unitAggregationType === 'count_distinct') {
        return MetricKind.COUNT_DISTINCT;
      } else if (isNumeric) {
        return MetricKind.NUMERIC;
      } else if (unitAggregationType === 'sum') {
        return MetricKind.COUNT;
      } else if (unitAggregationType === 'average') {
        return MetricKind.BINARY;
      }
    }
  } else {
    switch (kindString) {
      case 'funnel':
        return MetricGroupKind.FUNNEL_GROUP;
      case 'standard':
        return MetricGroupKind.STANDARD_GROUP;
      case 'click':
        return MetricKind.CLICK;
      case 'pageview':
        return MetricKind.PAGEVIEW;
      case 'custom':
        return MetricKind.CUSTOM;
      case 'numeric':
        return MetricKind.NUMERIC;
      case 'binary':
        return MetricKind.BINARY;
      default:
        return MetricKind.CUSTOM;
    }
  }
  return MetricKind.CUSTOM;
};
